import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import {
  addItem,
  checkItem,
  clearChecked,
  deleteItem,
  getItems,
  getSuggestions,
  itemKeys,
  uncheckItem,
  updateItem,
  type Item,
  type ListItemsSnapshot,
} from './itemsApi.ts';
import { uuidv7 } from './uuidv7.ts';
import { isRetryable } from '../../offline/isOfflineError.ts';
import { enqueue, type QueuedMutation } from '../../offline/mutationQueue.ts';

/**
 * Decides what to do with a failed mutation.
 *
 * If the request never reached the server, the optimistic change stays on screen and the
 * intent is queued — the user is in a shop and their tap was not a mistake. If the
 * server actively refused, the optimistic change is rolled back, because retrying would
 * only fail again.
 */
async function queueOrRollback(
  error: unknown,
  mutation: QueuedMutation,
  rollback: () => void,
): Promise<void> {
  if (!isRetryable(error)) {
    rollback();
    return;
  }

  await enqueue(mutation);
}

/**
 * How often to ask for the list when the hub is not carrying it.
 *
 * Battery against being wrong. The hub is what normally keeps this in step, and polling
 * alongside it would be pure waste — so this only runs while the connection is *not*
 * live, and never while the app is in the background. Without it, a network that blocks
 * WebSockets and server-sent events outright (which some mobile and public networks do)
 * leaves the list silently wrong for the entire visit, with the indicator honestly
 * reporting that it is offline and nobody any better off.
 */
const DisconnectedPollMs = 30_000;

export function useItems(listId: string | undefined, options: { isLive?: boolean } = {}) {
  return useQuery({
    queryKey: itemKeys.list(listId ?? ''),
    queryFn: () => getItems(listId!),
    enabled: Boolean(listId),
    select: (snapshot) => snapshot.items.filter((item) => !item.deletedAt),
    refetchInterval: options.isLive ? false : DisconnectedPollMs,
    refetchIntervalInBackground: false,
  });
}

export function useSuggestions(listId: string | undefined) {
  return useQuery({
    queryKey: itemKeys.suggestions(listId ?? ''),
    queryFn: () => getSuggestions(listId!),
    enabled: Boolean(listId),
    staleTime: 60_000,
  });
}

/**
 * Writes an item into the cache, keeping whichever version is newer.
 *
 * Shared by the optimistic updates here and by the SignalR handlers in the next
 * milestone, which is why it compares versions rather than blindly assigning: an
 * optimistic result and a hub echo of the same change can arrive in either order.
 */
/**
 * Marks every list's suggestion chips stale.
 *
 * Anything that changes which items exist changes the chips — including on lists other
 * than the one being edited, because the ranking comes from the group's history. Adding
 * and renaming remove a chip; deleting and clearing bring one back.
 *
 * Checking and unchecking deliberately do not call this: an item in the trolley is
 * still on the list, so the chips are unaffected.
 */
export function invalidateSuggestions(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: itemKeys.allSuggestions });
}

export function upsertItem(queryClient: QueryClient, listId: string, item: Item): void {
  queryClient.setQueryData<ListItemsSnapshot>(itemKeys.list(listId), (snapshot) => {
    if (!snapshot) {
      return snapshot;
    }

    const index = snapshot.items.findIndex((existing) => existing.id === item.id);

    if (index === -1) {
      return { ...snapshot, items: [...snapshot.items, item] };
    }

    // A stale echo of a change we have already superseded must not undo it.
    if (snapshot.items[index]!.version > item.version) {
      return snapshot;
    }

    const items = [...snapshot.items];
    items[index] = item;

    return { ...snapshot, items };
  });
}

/** What an add needs to know. See `useAddItem` for why the two notes are different things. */
export interface NewItem {
  id: string;
  name: string;
  /** A note typed with the item. Sent, and it wins over anything remembered. */
  note?: string;
  /**
   * The note the server is expected to bring back with this name, from the group's
   * history. Drawn on the optimistic row and deliberately never sent.
   */
  rememberedNote?: string | null;
}

/**
 * Adding an item.
 *
 * The id is minted before the request leaves, so the optimistic row and the server row
 * are the same row — no reconciling a temporary id later, and a retry is a no-op.
 *
 * `assignTo` and `rememberedNote` are what the server is *expected* to do, not
 * instructions: both assignment and note memory are decided server-side from the list's
 * settings, and these exist only so the row drawn on the instant of the tap already
 * carries the right colour and the right note. Getting either wrong is harmless — the
 * response, or the reconcile after an offline flush, corrects it. Sending them instead
 * would put a second decider in the client, working from a cache that can be a minute
 * old and a list setting that may have been turned off since.
 */
export function useAddItem(listId: string, assignTo: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name, note }: NewItem) =>
      addItem(listId, { id, name, ...(note ? { note } : {}) }),

    onMutate: async ({ id, name, note, rememberedNote }) => {
      await queryClient.cancelQueries({ queryKey: itemKeys.list(listId) });

      const previous = queryClient.getQueryData<ListItemsSnapshot>(itemKeys.list(listId));

      // Appear instantly. In a shop the round trip is often seconds, and a list that
      // does not visibly respond to a tap gets tapped again.
      queryClient.setQueryData<ListItemsSnapshot>(itemKeys.list(listId), (snapshot) =>
        snapshot
          ? {
              ...snapshot,
              items: [
                ...snapshot.items,
                optimisticItem({ id, name, note: note ?? rememberedNote, listId, assignTo }),
              ],
            }
          : snapshot,
      );

      return { previous };
    },

    onError: async (error, { id, name, note }, context) => {
      await queueOrRollback(
        error,
        // rememberedNote is not queued: the server resolves it when the add finally
        // lands, which is the only reading of the group's history that counts.
        { kind: 'add', listId, itemId: id, ...(note ? { note } : {}), name },
        () => {
          if (context?.previous) {
            queryClient.setQueryData(itemKeys.list(listId), context.previous);
          }
        },
      );
    },

    onSuccess: (item) => {
      upsertItem(queryClient, listId, item);
      void invalidateSuggestions(queryClient);
    },
  });
}

/**
 * Check and uncheck, as one hook.
 *
 * A single tap on the row toggles it, and the tap itself is the undo — so there is no
 * confirmation and no toast, and both directions must feel identical.
 */
export function useToggleItem(listId: string, currentUserId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ item }: { item: Item }) =>
      item.isChecked ? uncheckItem(item.id) : checkItem(item.id),

    onMutate: async ({ item }) => {
      await queryClient.cancelQueries({ queryKey: itemKeys.list(listId) });

      const previous = queryClient.getQueryData<ListItemsSnapshot>(itemKeys.list(listId));

      queryClient.setQueryData<ListItemsSnapshot>(itemKeys.list(listId), (snapshot) => {
        if (!snapshot) {
          return snapshot;
        }

        return {
          ...snapshot,
          items: snapshot.items.map((existing) =>
            existing.id === item.id
              ? {
                  ...existing,
                  isChecked: !existing.isChecked,
                  // Optimistically claim the colour. If someone beat us to it the
                  // server keeps their attribution and the response corrects us.
                  checkedByUserId: existing.isChecked ? null : (currentUserId ?? null),
                  checkedAt: existing.isChecked ? null : new Date().toISOString(),
                }
              : existing,
          ),
        };
      });

      return { previous };
    },

    onError: async (error, { item }, context) => {
      await queueOrRollback(
        error,
        // The queued intent is the direction the user tapped, not a toggle: replaying a
        // toggle after the state has moved on would flip it the wrong way.
        { kind: item.isChecked ? 'uncheck' : 'check', listId, itemId: item.id },
        () => {
          if (context?.previous) {
            queryClient.setQueryData(itemKeys.list(listId), context.previous);
          }
        },
      );
    },

    onSuccess: (item) => upsertItem(queryClient, listId, item),
  });
}

export interface ItemEdit {
  itemId: string;
  name: string;
  note?: string | null;
  /** Null means unassigned. Always sent, never omitted — see `updateItem`. */
  assignedToUserId?: string | null;
}

export function useUpdateItem(listId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, name, note, assignedToUserId }: ItemEdit) =>
      updateItem(itemId, {
        name,
        note: note ?? null,
        assignedToUserId: assignedToUserId ?? null,
      }),

    onMutate: async ({ itemId, name, note, assignedToUserId }) => {
      await queryClient.cancelQueries({ queryKey: itemKeys.list(listId) });

      const previous = queryClient.getQueryData<ListItemsSnapshot>(itemKeys.list(listId));

      // Applied optimistically like every other mutation. The editor closes as soon as
      // you save, so without this the row would show the old text until the server
      // answered — and offline, it would never change at all.
      queryClient.setQueryData<ListItemsSnapshot>(itemKeys.list(listId), (snapshot) =>
        snapshot
          ? {
              ...snapshot,
              items: snapshot.items.map((existing) =>
                existing.id === itemId
                  ? {
                      ...existing,
                      name,
                      note: note ?? null,
                      assignedToUserId: assignedToUserId ?? null,
                    }
                  : existing,
              ),
            }
          : snapshot,
      );

      return { previous };
    },

    onError: async (error, { itemId, name, note, assignedToUserId }, context) => {
      await queueOrRollback(
        error,
        {
          kind: 'update',
          listId,
          itemId,
          name,
          note: note ?? null,
          assignedToUserId: assignedToUserId ?? null,
        },
        () => {
          if (context?.previous) {
            queryClient.setQueryData(itemKeys.list(listId), context.previous);
          }
        },
      );
    },

    onSuccess: (item) => {
      upsertItem(queryClient, listId, item);
      // Renaming changes the normalised name, which is both what the chips are grouped
      // by and what the exclusion matches on.
      void invalidateSuggestions(queryClient);
    },
  });
}

export function useDeleteItem(listId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) => deleteItem(itemId),

    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: itemKeys.list(listId) });

      const previous = queryClient.getQueryData<ListItemsSnapshot>(itemKeys.list(listId));

      queryClient.setQueryData<ListItemsSnapshot>(itemKeys.list(listId), (snapshot) =>
        snapshot
          ? { ...snapshot, items: snapshot.items.filter((item) => item.id !== itemId) }
          : snapshot,
      );

      return { previous };
    },

    onError: async (error, itemId, context) => {
      await queueOrRollback(error, { kind: 'delete', listId, itemId }, () => {
        if (context?.previous) {
          queryClient.setQueryData(itemKeys.list(listId), context.previous);
        }
      });
    },

    // Removing something frees its name up again, so it should come back as a chip.
    onSuccess: () => void invalidateSuggestions(queryClient),
  });
}

export function useClearChecked(listId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => clearChecked(listId),
    onError: async (error) => {
      await queueOrRollback(error, { kind: 'clearChecked', listId }, () => {
        // Nothing to roll back: clearing is not applied optimistically, so a refused
        // clear simply leaves the ticked items where they were.
      });
    },
    onSuccess: (clearedIds) => {
      const cleared = new Set(clearedIds);

      queryClient.setQueryData<ListItemsSnapshot>(itemKeys.list(listId), (snapshot) =>
        snapshot
          ? { ...snapshot, items: snapshot.items.filter((item) => !cleared.has(item.id)) }
          : snapshot,
      );

      // Clearing removes a whole shop's worth of items, so most of the chip row is
      // now wrong — everything just cleared is offerable again.
      void invalidateSuggestions(queryClient);
    },
  });
}

export { uuidv7 };

/**
 * The row this client predicts the server is about to hold.
 *
 * Exported because it is drawn from two places that must not disagree: the instant of the
 * tap, and again after a reload, when `applyPending` redraws the same add from the queue
 * because the response it is waiting for has still not arrived.
 *
 * `at` is the moment the intent was recorded rather than the moment it is drawn — a
 * replayed row must not appear to have been created afresh on every render.
 */
export function optimisticItem({
  id,
  name,
  note,
  listId,
  assignTo,
  at,
}: {
  id: string;
  name: string;
  note: string | null | undefined;
  listId: string;
  assignTo: string | null;
  at?: string;
}): Item {
  const now = at ?? new Date().toISOString();

  return {
    id,
    listId,
    name,
    note: note ?? null,
    addedByUserId: '',
    assignedToUserId: assignTo,
    createdAt: now,
    isChecked: false,
    checkedByUserId: null,
    checkedAt: null,
    // Sorts to the end, where a newly added item belongs.
    sortOrder: Number.MAX_SAFE_INTEGER,
    deletedAt: null,
    updatedAt: now,
    // Version 0 so the real server row (version 1+) always wins the comparison in
    // upsertItem, whichever order the two arrive in.
    version: 0,
  };
}
